import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Paper,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import EditIcon from '@mui/icons-material/Edit';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { getPublicEntity } from '../../services/publicEntityService';
import useAuth from '../../hooks/useAuth';
import swal from 'sweetalert';
import { useTheme } from '@mui/material/styles';

interface PublicEntityDetail {
  id: string;
  name: string;
  city: {
    id: string;
    name: string;
  };
  departments: Department[];
}

interface Department {
  id: string;
  name: string;
  budget_units: BudgetUnit[];
}

interface BudgetUnit {
  id: string;
  name: string;
}

const PublicEntityDetailView: React.FC = () => {
  const { entityId } = useParams<{ entityId: string }>();
  const [entity, setEntity] = useState<PublicEntityDetail | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const { user } = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();

  /**
   * Função para buscar os detalhes da entidade pública.
   */
  const fetchEntity = async () => {
    setLoading(true);
    try {
      if (entityId) {
        const data = await getPublicEntity(entityId);
        setEntity(data);
      }
    } catch (error) {
      console.error('Erro ao buscar entidade pública:', error);
      swal('Erro', 'Não foi possível buscar os detalhes da entidade pública.', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEntity();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityId]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!entity) {
    return (
      <Typography variant="h6" color="error" sx={{ textAlign: 'center', mt: 4 }}>
        Entidade Pública não encontrada.
      </Typography>
    );
  }

  /**
   * Função para formatar datas no formato brasileiro.
   */
  const formatDate = (dateString: string): string => {
    const options: Intl.DateTimeFormatOptions = {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    };
    return new Date(dateString).toLocaleDateString('pt-BR', options);
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 4, md: 6 } }}>
      {/* Cabeçalho com Botão de Voltar e Botão de Edição */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Button
          variant="text"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(-1)} // Navega para a página anterior
          sx={{ textTransform: 'none' }}
        >
          Voltar
        </Button>
        <Typography variant="h4">Detalhes da Entidade Pública</Typography>
        {user?.is_system_admin && (
          <Button
            variant="contained"
            color="primary"
            startIcon={<EditIcon />}
            onClick={() => navigate(`/entidades-publicas/${entity.id}/editar`)}
          >
            Editar Entidade
          </Button>
        )}
      </Box>

      {/* Informações Básicas da Entidade */}
      <Paper sx={{ p: 4, mb: 4 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box>
            <Typography variant="h6" gutterBottom>
              Nome:
            </Typography>
            <Typography variant="body1">{entity.name} ({ entity.id })</Typography>
          </Box>
          <Divider />
          <Box>
            <Typography variant="h6" gutterBottom>
              Cidade:
            </Typography>
            <Typography variant="body1">{entity.city.name}</Typography>
          </Box>
        </Box>
      </Paper>

      {/* Seção de Órgãos */}
      <Box>
        <Typography variant="h5" gutterBottom sx={{ mb: 2 }}>
          Órgãos
        </Typography>
        {entity.departments.length > 0 ? (
          entity.departments.map((dept) => (
            <Accordion key={dept.id} sx={{ mb: 2 }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle1">{dept.name}</Typography>
              </AccordionSummary>
              <AccordionDetails>
                {dept.budget_units.length > 0 ? (
                  <TableContainer component={Paper}>
                    <Table aria-label={`Unidades Orçamentárias do Departamento ${dept.name}`}>
                      <TableHead>
                        <TableRow>
                          <TableCell>Código da Unidade</TableCell>
                          <TableCell>Unidade Orçamentária</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {dept.budget_units.map((bu) => (
                          <TableRow key={bu.id} hover>
                            <TableCell>{bu.id}</TableCell>
                            <TableCell>{bu.name}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    Nenhuma unidade orçamentária encontrada.
                  </Typography>
                )}
              </AccordionDetails>
            </Accordion>
          ))
        ) : (
          <Typography variant="body1" color="text.secondary">
            Nenhum departamento encontrado.
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default PublicEntityDetailView;